// ============================================================
// FILE: src/pages/Home.jsx
// ============================================================
import React, { useEffect, useRef, useState } from 'react';
import Hero from '../componenets/Hero';
import CTA from '../componenets/CTA';
import { AnimatePresence, motion } from 'framer-motion';

import {
    Building2,
    Warehouse,
    Factory,
    Shield,
    Award,
    Users,
    HardHat,
    Wrench,
    X,
    Sparkles,
    CheckCircle2,
    Star,
    Quote,
    ShieldCheck,
    Clock,
    Medal,
    Globe,
    Layers,
    MoveRight,
    ChevronLeft,
    ChevronRight as ChevronRightIcon,
} from 'lucide-react';

// ============================================================
// ✅ GOOGLE APPS SCRIPT WEB APP URL
// ============================================================
const SCRIPT_URL =
    'https://script.google.com/macros/s/AKfycbyTcJMw4wrWlo27N6GkHbSmxgyq3sK84c3flJpHJJZqiCAVlZ6tCawz3El8Ug3_ffhj/exec';

const AnimatedCounter = ({ value, suffix = '', duration = 1400, formatter }) => {
    const [displayValue, setDisplayValue] = useState(0);
    const [hasAnimated, setHasAnimated] = useState(false);
    const ref = useRef(null);

    useEffect(() => {
        if (!ref.current || hasAnimated) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setHasAnimated(true);

                    const startTime = performance.now();
                    const startValue = 0;

                    const tick = (currentTime) => {
                        const progress = Math.min(
                            (currentTime - startTime) / duration,
                            1
                        );

                        const eased = 1 - Math.pow(1 - progress, 3);

                        setDisplayValue(
                            Math.round(
                                startValue +
                                (value - startValue) * eased
                            )
                        );

                        if (progress < 1) {
                            requestAnimationFrame(tick);
                        }
                    };

                    requestAnimationFrame(tick);
                }
            },
            { threshold: 0.4 }
        );

        observer.observe(ref.current);

        return () => observer.disconnect();
    }, [duration, value, hasAnimated]);

    return (
        <div
            ref={ref}
            className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold bg-gradient-to-r from-[var(--color-dark)] to-[var(--color-accent)] bg-clip-text text-transparent"
        >
            {formatter ? formatter(displayValue) : `${displayValue}${suffix}`}
        </div>
    );
};

const Home = () => {
    const services = [
        {
            icon: Building2,
            title: 'Structural Steel Fabrication',
            description:
                'Precision-engineered steel structures combining strength with architectural elegance.',
            image: 'https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?auto=format&fit=crop&w=1200&q=80',
            gradient: 'from-blue-600/40 to-cyan-600/40',
            iconBg: 'bg-blue-500/30',
            iconColor: 'text-blue-300',
        },
        {
            icon: Warehouse,
            title: 'Industrial Sheds',
            description:
                'Custom-designed industrial spaces maximizing functionality and aesthetic appeal.',
            image: 'https://images.unsplash.com/photo-1565610222536-ef125c59da2e?auto=format&fit=crop&w=1200&q=80',
            gradient: 'from-amber-600/40 to-orange-600/40',
            iconBg: 'bg-amber-500/30',
            iconColor: 'text-amber-300',
        },
        {
            icon: Factory,
            title: 'Warehouse Construction',
            description:
                'End-to-end warehouse solutions for optimal storage efficiency and safety.',
            image: 'https://images.unsplash.com/photo-1553413077-190dd305871c?auto=format&fit=crop&w=1200&q=80',
            gradient: 'from-emerald-600/40 to-teal-600/40',
            iconBg: 'bg-emerald-500/30',
            iconColor: 'text-emerald-300',
        },
        {
            icon: Shield,
            title: 'Infrastructure Projects',
            description:
                'Large-scale infrastructure development with focus on sustainability and durability.',
            image: 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?auto=format&fit=crop&w=1200&q=80',
            gradient: 'from-purple-600/40 to-pink-600/40',
            iconBg: 'bg-purple-500/30',
            iconColor: 'text-purple-300',
        },
        {
            icon: HardHat,
            title: 'Site Services',
            description:
                'Comprehensive on-site support ensuring seamless project execution and safety.',
            image: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=1200&q=80',
            gradient: 'from-red-600/40 to-rose-600/40',
            iconBg: 'bg-red-500/30',
            iconColor: 'text-red-300',
        },
        {
            icon: Wrench,
            title: 'Maintenance & Repairs',
            description:
                'Proactive maintenance extending structure lifecycle with minimal disruption.',
            image: 'https://images.unsplash.com/photo-1581092160607-ee22621dd758?auto=format&fit=crop&w=1200&q=80',
            gradient: 'from-indigo-600/40 to-violet-600/40',
            iconBg: 'bg-indigo-500/30',
            iconColor: 'text-indigo-300',
        },
    ];

    // ✅ ALL 8 PROJECTS — sab slider mein aayenge
    const projects = [
        {
            title: 'Mega Industrial Park',
            category: 'Infrastructure',
            image: '/newimg.jpeg',
        },
        {
            title: 'Mega Industrial Park',
            category: 'Infrastructure',
            image: '/newimg1.jpeg',
        },
        {
            title: 'Steel Bridge Construction',
            category: 'Structural Steel',
            image: '/newimg2.jpeg',
        },
        {
            title: 'Warehouse Complex',
            category: 'Warehouse',
            image: '/newimg3.jpeg',
        },
        {
            title: 'Project One',
            category: 'Structural Steel',
            image: '/img1.jpeg',
        },
        {
            title: 'Project Two',
            category: 'Warehouse',
            image: '/img2.jpeg',
        },
        {
            title: 'Project Three',
            category: 'Infrastructure',
            image: '/img3.jpeg',
        },
        {
            title: 'Project Four',
            category: 'Industrial Shed',
            image: '/img4.jpeg',
        },
    ];

    const testimonials = [
        {
            content:
                'Exceptional quality and timely delivery. Sanwariya Ji exceeded our expectations with their attention to detail and professional approach.',
            rating: 5,
            name: 'Rahul Sharma',
            company: 'ABC Corp',
        },
        {
            content:
                'Their expertise in steel fabrication is unmatched. They delivered a complex project ahead of schedule with impeccable quality standards.',
            rating: 5,
            name: 'Priya Patel',
            company: 'InfraBuild',
        },
        {
            content:
                'Professional team with great attention to detail. We look forward to working with them again on our upcoming projects.',
            rating: 5,
            name: 'Amit Singh',
            company: 'Metro Constructions',
        },
        {
            content:
                'The best in the industry! Their commitment to quality and safety standards is truly commendable.',
            rating: 5,
            name: 'Sneha Reddy',
            company: 'BuildTech Solutions',
        },
        {
            content:
                'Highly recommend Sanwariya Ji for any steel infrastructure project. They deliver excellence consistently.',
            rating: 5,
            name: 'Vikram Malhotra',
            company: 'Global Infra',
        },
    ];

    const stats = [
        { icon: Award, value: 40, suffix: '+', label: 'Years' },
        { icon: Building2, value: 10000, suffix: '+', label: 'Projects' },
        { icon: Users, value: 6000, suffix: '+', label: 'Clients' },
    ];

    const industries = [
        { name: 'Automotive', icon: '🚗' },
        { name: 'Construction', icon: '🏗️' },
        { name: 'Energy', icon: '⚡' },
        { name: 'Manufacturing', icon: '🏭' },
        { name: 'Mining', icon: '⛏️' },
        { name: 'Oil & Gas', icon: '🛢️' },
        { name: 'Infrastructure', icon: '🌉' },
        { name: 'Warehousing', icon: '📦' },
    ];

    const features = [
        {
            icon: ShieldCheck,
            title: 'Quality Assured',
            desc: 'ISO certified processes ensuring excellence',
        },
        {
            icon: Clock,
            title: 'On-Time Delivery',
            desc: 'Committed to deadlines with precision',
        },
        {
            icon: Medal,
            title: 'Industry Experts',
            desc: 'Decades of combined experience',
        },
        {
            icon: Globe,
            title: 'Pan-India Reach',
            desc: 'Serving clients across the nation',
        },
    ];

    const [projectIndex, setProjectIndex] = useState(0);
    const [testimonialIndex, setTestimonialIndex] = useState(0);

    const nextTestimonial = () => {
        setTestimonialIndex(
            (prev) => (prev + 1) % testimonials.length
        );
    };

    const prevTestimonial = () => {
        setTestimonialIndex(
            (prev) =>
                (prev - 1 + testimonials.length) % testimonials.length
        );
    };

    const nextProject = () => {
        setProjectIndex((prev) => (prev + 1) % projects.length);
    };

    const prevProject = () => {
        setProjectIndex(
            (prev) => (prev - 1 + projects.length) % projects.length
        );
    };

    useEffect(() => {
        const testimonialInterval = setInterval(() => {
            setTestimonialIndex(
                (prev) => (prev + 1) % testimonials.length
            );
        }, 4000);

        return () => clearInterval(testimonialInterval);
    }, [testimonials.length]);

    useEffect(() => {
        const projectInterval = setInterval(() => {
            setProjectIndex((prev) => (prev + 1) % projects.length);
        }, 5000);

        return () => clearInterval(projectInterval);
    }, [projects.length]);

    const leadTriggerRef = useRef(null);
    const modalAllowedRef = useRef(true);

    const [showLeadModal, setShowLeadModal] = useState(false);

    const [leadFormData, setLeadFormData] = useState({
        name: '',
        phone: '',
        email: '',
        message: '',
    });

    const [leadSubmitted, setLeadSubmitted] = useState(false);
    const [leadSubmitting, setLeadSubmitting] = useState(false);

    const handleLeadChange = (e) => {
        setLeadFormData({
            ...leadFormData,
            [e.target.name]: e.target.value,
        });
    };

    // ============================================================
    // ✅ LEAD SUBMIT → GOOGLE SHEETS
    // ============================================================
    const handleLeadSubmit = async (e) => {
        e.preventDefault();
        setLeadSubmitting(true);

        try {
            await fetch(SCRIPT_URL, {
                method: 'POST',
                mode: 'no-cors', // required for Google Apps Script
                headers: {
                    'Content-Type': 'text/plain;charset=utf-8',
                },
                body: JSON.stringify(leadFormData),
            });

            // no-cors mode can't read response, so assume success
            setLeadSubmitted(true);

            // Reset form + close success state after 3.5s
            setTimeout(() => {
                setLeadFormData({
                    name: '',
                    phone: '',
                    email: '',
                    message: '',
                });
                setLeadSubmitted(false);
            }, 3500);
        } catch (error) {
            console.error('Lead submit failed:', error);
            alert(
                'Something went wrong. Please try again or call us directly.'
            );
        } finally {
            setLeadSubmitting(false);
        }
    };

    useEffect(() => {
        if (!leadTriggerRef.current) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting && modalAllowedRef.current) {
                    setShowLeadModal(true);
                    modalAllowedRef.current = false;
                }

                if (!entry.isIntersecting) {
                    modalAllowedRef.current = true;
                }
            },
            { threshold: 0.35 }
        );

        observer.observe(leadTriggerRef.current);

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        document.body.style.overflow = showLeadModal ? 'hidden' : '';

        return () => {
            document.body.style.overflow = '';
        };
    }, [showLeadModal]);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
            className="bg-[#FAF8F6]"
        >
            <Hero />

            {/* Company Introduction */}
            <section className="relative py-8 sm:py-12 md:py-16 lg:py-20 px-4 sm:px-6 overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-b from-[var(--color-accent)]/5 via-transparent to-transparent" />

                <div className="absolute top-0 right-0 w-64 sm:w-96 h-64 sm:h-96 bg-[var(--color-accent)]/10 rounded-full blur-3xl translate-x-1/3 -translate-y-1/2 pointer-events-none animate-pulse-slow" />

                <div className="absolute bottom-0 left-0 w-64 sm:w-96 h-64 sm:h-96 bg-[var(--color-secondary)]/10 rounded-full blur-3xl -translate-x-1/3 translate-y-1/2 pointer-events-none animate-pulse-slow" />

                <div className="container-custom relative">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7, ease: 'easeOut' }}
                        className="max-w-4xl mx-auto text-center"
                    >
                        <motion.div
                            initial={{ scale: 0.9 }}
                            whileInView={{ scale: 1 }}
                            className="inline-flex items-center gap-2 px-3 sm:px-4 md:px-5 py-1.5 sm:py-2 md:py-2.5 rounded-full bg-gradient-to-r from-[var(--color-accent)]/10 to-[var(--color-secondary)]/10 border border-[var(--color-accent)]/20 mb-4 sm:mb-6 shadow-lg shadow-[var(--color-accent)]/5"
                        >
                            <Sparkles
                                size={14}
                                className="sm:w-4 sm:h-4 text-[var(--color-accent)]"
                            />

                            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-[var(--color-accent)]">
                                Trusted Since 1986
                            </span>
                        </motion.div>

                        <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-[var(--color-dark)] leading-[1.1]">
                            Building Tomorrow's
                            <span className="block bg-gradient-to-r from-[var(--color-accent)] via-[var(--color-secondary)] to-[var(--color-accent)] bg-clip-text text-transparent bg-[length:200%] animate-gradient mt-1 sm:mt-2">
                                Infrastructure Today
                            </span>
                        </h2>

                        <p className="mt-3 sm:mt-4 md:mt-5 text-sm sm:text-base md:text-lg lg:text-xl text-slate-600 leading-relaxed max-w-3xl mx-auto">
                            Sanwariya Ji Steels & Infrastructure LLP stands at the forefront of steel fabrication
                            and infrastructure development, combining decades of expertise with innovative engineering.
                        </p>

                        <div className="hidden sm:flex flex-wrap justify-center gap-2 sm:gap-3 mt-4 sm:mt-6">
                            {features.map((feature, idx) => (
                                <motion.div
                                    key={idx}
                                    initial={{ opacity: 0, y: 10 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ delay: idx * 0.1 }}
                                    className="flex items-center gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-white/80 backdrop-blur-sm rounded-full border border-[var(--color-border)]/30 shadow-lg shadow-black/5 hover:shadow-xl hover:shadow-[var(--color-accent)]/10 transition-all duration-300 hover:-translate-y-1"
                                >
                                    <feature.icon
                                        size={14}
                                        className="sm:w-4 sm:h-4 text-[var(--color-accent)]"
                                    />

                                    <span className="text-xs sm:text-sm font-medium text-slate-700">
                                        {feature.title}
                                    </span>
                                </motion.div>
                            ))}
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* Stats */}
            <section className="py-4 sm:py-6 md:py-8 px-4 sm:px-6">
                <div className="container-custom">
                    <div className="grid grid-cols-3 gap-2 sm:gap-4 md:gap-6">
                        {stats.map((stat, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="group relative bg-white rounded-xl sm:rounded-2xl p-3 sm:p-5 md:p-6 lg:p-8 shadow-[0_8px_30px_rgb(0,0,0,0.06)] hover:shadow-[0_20px_60px_rgba(0,0,0,0.12)] transition-all duration-500 border border-[var(--color-border)]/20 hover:border-[var(--color-accent)]/30 hover:-translate-y-1 sm:hover:-translate-y-2"
                            >
                                <div className="absolute inset-0 bg-gradient-to-br from-[var(--color-accent)]/5 to-transparent rounded-xl sm:rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                                <div className="relative text-center">
                                    <div className="inline-flex p-1.5 sm:p-2 md:p-3 rounded-lg sm:rounded-xl bg-gradient-to-br from-[var(--color-accent)]/10 to-[var(--color-secondary)]/10 group-hover:scale-110 group-hover:shadow-lg group-hover:shadow-[var(--color-accent)]/20 transition-all duration-300 mb-1 sm:mb-2 md:mb-4">
                                        <stat.icon className="text-[var(--color-accent)] w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6" />
                                    </div>

                                    <AnimatedCounter
                                        value={stat.value}
                                        suffix={stat.suffix}
                                        formatter={(value) => {
                                            if (stat.label === 'Projects')
                                                return value >= 10000
                                                    ? '10K+'
                                                    : `${value}+`;

                                            if (stat.label === 'Clients')
                                                return value >= 6000
                                                    ? '6K+'
                                                    : `${value}+`;

                                            return `${value}+`;
                                        }}
                                    />

                                    <div className="text-[10px] sm:text-xs md:text-sm lg:text-base text-slate-500 font-medium mt-0.5 sm:mt-1">
                                        {stat.label}
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services — each card has its OWN background image */}
            <section className="py-10 sm:py-16 md:py-20 lg:py-28 px-4 sm:px-6">
                <div className="container-custom">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7 }}
                        className="text-center mb-8 sm:mb-12 md:mb-16"
                    >
                        <motion.div
                            initial={{ scale: 0.9 }}
                            whileInView={{ scale: 1 }}
                            className="inline-flex items-center gap-2 px-3 sm:px-4 md:px-5 py-1.5 sm:py-2 md:py-2.5 rounded-full bg-gradient-to-r from-[var(--color-accent)]/10 to-[var(--color-secondary)]/10 border border-[var(--color-accent)]/20 mb-3 sm:mb-4 shadow-lg shadow-[var(--color-accent)]/5"
                        >
                            <Star
                                size={14}
                                className="sm:w-4 sm:h-4 text-[var(--color-accent)]"
                            />

                            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-[var(--color-accent)]">
                                Core Capabilities
                            </span>
                        </motion.div>

                        <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[var(--color-dark)]">
                            Comprehensive{' '}
                            <span className="gradient-text">Solutions</span>
                        </h2>

                        <p className="mt-2 sm:mt-3 md:mt-4 text-xs sm:text-base md:text-lg text-slate-500 max-w-2xl mx-auto">
                            Delivering excellence across every facet of steel and infrastructure development
                        </p>
                    </motion.div>

                    <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5 md:gap-6 lg:gap-8">
                        {services.map((service, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 20 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.08 }}
                                className="group relative overflow-hidden rounded-xl sm:rounded-2xl min-h-[220px] sm:min-h-[260px] md:min-h-[300px] shadow-[0_8px_30px_rgb(0,0,0,0.08)] hover:shadow-[0_30px_80px_rgba(0,0,0,0.25)] transition-all duration-500 hover:-translate-y-1 sm:hover:-translate-y-2 md:hover:-translate-y-3"
                            >
                                <img
                                    src={service.image}
                                    alt={service.title}
                                    className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110"
                                />

                                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/95 via-slate-900/70 to-slate-900/40 group-hover:from-slate-900/95 group-hover:via-slate-900/60 transition-all duration-500" />

                                <div
                                    className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-0 group-hover:opacity-60 transition-opacity duration-500 mix-blend-overlay`}
                                />

                                <div className="relative z-10 flex h-full flex-col justify-end p-3 sm:p-5 md:p-6 lg:p-7">
                                    <div
                                        className={`mb-2 sm:mb-3 md:mb-4 inline-flex h-9 w-9 sm:h-11 sm:w-11 md:h-12 md:w-12 items-center justify-center rounded-lg sm:rounded-xl ${service.iconBg} backdrop-blur-md border border-white/20 group-hover:scale-110 transition-transform duration-300`}
                                    >
                                        <service.icon
                                            className={`${service.iconColor} w-4 h-4 sm:w-5 sm:h-5 md:w-6 md:h-6`}
                                        />
                                    </div>

                                    <h3 className="text-sm sm:text-lg md:text-xl lg:text-2xl font-bold text-white mb-1 sm:mb-2 leading-tight drop-shadow-lg">
                                        {service.title}
                                    </h3>

                                    <p className="hidden sm:block text-xs sm:text-sm md:text-base text-white/80 leading-relaxed drop-shadow">
                                        {service.description}
                                    </p>

                                    <div className="mt-2 sm:mt-3 md:mt-4 flex items-center gap-1 sm:gap-2 text-[var(--color-accent)] font-semibold text-[10px] sm:text-xs md:text-sm opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
                                        <span className="hidden sm:inline">Learn More</span>
                                        <span className="sm:hidden">View</span>
                                        <MoveRight
                                            size={12}
                                            className="sm:w-4 sm:h-4 group-hover:translate-x-1 transition-transform"
                                        />
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Featured Projects — ONLY SLIDER (no grid) */}
            <section className="py-10 sm:py-16 md:py-20 lg:py-28 px-4 sm:px-6 bg-white">
                <div className="container-custom">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.7 }}
                        className="text-center mb-8 sm:mb-12 md:mb-16"
                    >
                        <motion.div
                            initial={{ scale: 0.9 }}
                            whileInView={{ scale: 1 }}
                            className="inline-flex items-center gap-2 px-3 sm:px-4 md:px-5 py-1.5 sm:py-2 md:py-2.5 rounded-full bg-gradient-to-r from-[var(--color-accent)]/10 to-[var(--color-secondary)]/10 border border-[var(--color-accent)]/20 mb-3 sm:mb-4 shadow-lg shadow-[var(--color-accent)]/5"
                        >
                            <Building2
                                size={14}
                                className="sm:w-4 sm:h-4 text-[var(--color-accent)]"
                            />

                            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-[var(--color-accent)]">
                                Our Portfolio
                            </span>
                        </motion.div>

                        <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[var(--color-dark)]">
                            Featured{' '}
                            <span className="gradient-text">Projects</span>
                        </h2>
                    </motion.div>

                    {/* Slider — visible on ALL screen sizes */}
                    <div className="relative max-w-4xl mx-auto">
                        <div className="overflow-hidden rounded-xl sm:rounded-2xl">
                            <motion.div
                                key={projectIndex}
                                initial={{ opacity: 0, x: 40 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -40 }}
                                transition={{ duration: 0.5 }}
                                className="relative"
                            >
                                <div className="aspect-[16/10] sm:aspect-[16/9] overflow-hidden rounded-xl sm:rounded-2xl">
                                    <img
                                        src={projects[projectIndex].image}
                                        alt={projects[projectIndex].title || 'Project'}
                                        className="w-full h-full object-cover"
                                    />
                                </div>

                                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-transparent" />

                                <div className="absolute bottom-0 left-0 right-0 p-4 sm:p-6 md:p-8">
                                    <span className="inline-block px-2.5 sm:px-3 py-0.5 sm:py-1 rounded-full bg-gradient-to-r from-[var(--color-accent)] to-[var(--color-secondary)] text-white text-[10px] sm:text-xs font-bold uppercase tracking-wider mb-1.5 sm:mb-2 shadow-lg shadow-[var(--color-accent)]/30">
                                        {projects[projectIndex].category}
                                    </span>

                                    <h3 className="text-white font-bold text-base sm:text-2xl md:text-3xl">
                                        {projects[projectIndex].title}
                                    </h3>
                                </div>
                            </motion.div>
                        </div>

                        <button
                            onClick={prevProject}
                            className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm p-2 sm:p-2.5 rounded-full shadow-lg hover:bg-white transition-all z-10"
                        >
                            <ChevronLeft
                                size={18}
                                className="sm:w-5 sm:h-5 text-[var(--color-dark)]"
                            />
                        </button>

                        <button
                            onClick={nextProject}
                            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm p-2 sm:p-2.5 rounded-full shadow-lg hover:bg-white transition-all z-10"
                        >
                            <ChevronRightIcon
                                size={18}
                                className="sm:w-5 sm:h-5 text-[var(--color-dark)]"
                            />
                        </button>

                        {/* Dots */}
                        <div className="flex justify-center gap-1.5 mt-4 sm:mt-5">
                            {projects.map((_, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => setProjectIndex(idx)}
                                    className={`h-1.5 rounded-full transition-all duration-300 ${idx === projectIndex
                                        ? 'w-6 bg-[var(--color-accent)]'
                                        : 'w-1.5 bg-slate-300'
                                        }`}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Industries */}
            <section className="py-10 sm:py-16 md:py-20 lg:py-24 px-4 sm:px-6">
                <div className="container-custom">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-8 sm:mb-12 md:mb-16"
                    >
                        <motion.div
                            initial={{ scale: 0.9 }}
                            whileInView={{ scale: 1 }}
                            className="inline-flex items-center gap-2 px-3 sm:px-4 md:px-5 py-1.5 sm:py-2 md:py-2.5 rounded-full bg-gradient-to-r from-[var(--color-accent)]/10 to-[var(--color-secondary)]/10 border border-[var(--color-accent)]/20 mb-3 sm:mb-4 shadow-lg shadow-[var(--color-accent)]/5"
                        >
                            <Layers
                                size={14}
                                className="sm:w-4 sm:h-4 text-[var(--color-accent)]"
                            />

                            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-[var(--color-accent)]">
                                Industries We Serve
                            </span>
                        </motion.div>

                        <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[var(--color-dark)]">
                            Trusted Across{' '}
                            <span className="gradient-text">Sectors</span>
                        </h2>
                    </motion.div>

                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 sm:gap-3 md:gap-4">
                        {industries.map((industry, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, scale: 0.9 }}
                                whileInView={{ opacity: 1, scale: 1 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.04 }}
                                className="group relative bg-white rounded-xl sm:rounded-2xl p-2.5 sm:p-3 md:p-4 lg:p-5 shadow-[0_8px_30px_rgb(0,0,0,0.06)] hover:shadow-[0_20px_60px_rgba(0,0,0,0.12)] transition-all duration-300 border border-[var(--color-border)]/20 hover:border-[var(--color-accent)]/30 hover:-translate-y-1 sm:hover:-translate-y-2 cursor-default"
                            >
                                <div className="absolute inset-0 bg-gradient-to-br from-[var(--color-accent)]/5 to-[var(--color-secondary)]/5 rounded-xl sm:rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                                <div className="relative text-center">
                                    <div className="text-xl sm:text-2xl md:text-3xl lg:text-4xl mb-0.5 sm:mb-1 md:mb-2 group-hover:scale-110 transition-transform duration-300">
                                        {industry.icon}
                                    </div>

                                    <span className="text-[10px] sm:text-xs md:text-sm lg:text-base font-semibold text-slate-700 group-hover:text-[var(--color-accent)] transition-colors">
                                        {industry.name}
                                    </span>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section className="py-10 sm:py-16 md:py-20 lg:py-28 px-4 sm:px-6 bg-gradient-to-b from-[#FAF8F6] to-white">
                <div className="container-custom">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        className="text-center mb-8 sm:mb-12 md:mb-16"
                    >
                        <motion.div
                            initial={{ scale: 0.9 }}
                            whileInView={{ scale: 1 }}
                            className="inline-flex items-center gap-2 px-3 sm:px-4 md:px-5 py-1.5 sm:py-2 md:py-2.5 rounded-full bg-gradient-to-r from-[var(--color-accent)]/10 to-[var(--color-secondary)]/10 border border-[var(--color-accent)]/20 mb-3 sm:mb-4 shadow-lg shadow-[var(--color-accent)]/5"
                        >
                            <Quote
                                size={14}
                                className="sm:w-4 sm:h-4 text-[var(--color-accent)]"
                            />

                            <span className="text-[10px] sm:text-xs font-bold uppercase tracking-widest text-[var(--color-accent)]">
                                Client Testimonials
                            </span>
                        </motion.div>

                        <h2 className="text-2xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-[var(--color-dark)]">
                            What Our{' '}
                            <span className="gradient-text">Clients Say</span>
                        </h2>
                    </motion.div>

                    <div className="sm:hidden relative">
                        <div className="overflow-hidden rounded-xl">
                            <motion.div
                                key={testimonialIndex}
                                initial={{ opacity: 0, scale: 0.95 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ duration: 0.6 }}
                                className="bg-white p-5 rounded-xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] border border-[var(--color-border)]/20"
                            >
                                <div className="relative">
                                    <div className="absolute -top-1 -right-1 text-[var(--color-accent)]/10">
                                        <Quote size={36} />
                                    </div>

                                    <div className="flex gap-0.5 text-[var(--color-accent)] mb-3">
                                        {[
                                            ...Array(
                                                testimonials[testimonialIndex]
                                                    .rating
                                            ),
                                        ].map((_, i) => (
                                            <Star
                                                key={i}
                                                size={16}
                                                fill="currentColor"
                                            />
                                        ))}
                                    </div>

                                    <p className="text-sm text-slate-600 leading-relaxed relative z-10">
                                        "{testimonials[testimonialIndex].content}"
                                    </p>

                                    <div className="mt-3 flex items-center justify-between">
                                        <div>
                                            <p className="font-bold text-[var(--color-dark)] text-sm">
                                                {testimonials[testimonialIndex].name}
                                            </p>

                                            <p className="text-xs text-slate-500">
                                                {testimonials[testimonialIndex].company}
                                            </p>
                                        </div>

                                        <div className="w-8 h-0.5 bg-gradient-to-r from-[var(--color-accent)] to-[var(--color-secondary)] rounded-full" />
                                    </div>
                                </div>
                            </motion.div>
                        </div>

                        <button
                            onClick={prevTestimonial}
                            className="absolute left-1 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm p-1.5 rounded-full shadow-lg hover:bg-white transition-all z-10"
                        >
                            <ChevronLeft
                                size={16}
                                className="text-[var(--color-dark)]"
                            />
                        </button>

                        <button
                            onClick={nextTestimonial}
                            className="absolute right-1 top-1/2 -translate-y-1/2 bg-white/90 backdrop-blur-sm p-1.5 rounded-full shadow-lg hover:bg-white transition-all z-10"
                        >
                            <ChevronRightIcon
                                size={16}
                                className="text-[var(--color-dark)]"
                            />
                        </button>

                        <div className="flex justify-center gap-1 mt-3">
                            {testimonials.map((_, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => setTestimonialIndex(idx)}
                                    className={`h-1 rounded-full transition-all duration-300 ${idx === testimonialIndex
                                        ? 'w-4 bg-[var(--color-accent)]'
                                        : 'w-1 bg-slate-300'
                                        }`}
                                />
                            ))}
                        </div>
                    </div>

                    <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 lg:gap-8">
                        {testimonials.slice(0, 3).map((testimonial, index) => (
                            <motion.div
                                key={index}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: index * 0.1 }}
                                className="group relative bg-white p-6 sm:p-8 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.06)] hover:shadow-[0_30px_80px_rgba(0,0,0,0.12)] transition-all duration-500 border border-[var(--color-border)]/20 hover:border-[var(--color-accent)]/30 hover:-translate-y-3"
                            >
                                <div className="absolute inset-0 bg-gradient-to-br from-[var(--color-accent)]/5 to-[var(--color-secondary)]/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

                                <div className="relative">
                                    <div className="absolute -top-2 -right-2 text-[var(--color-accent)]/10">
                                        <Quote size={48} />
                                    </div>

                                    <div className="flex gap-1 text-[var(--color-accent)] mb-4">
                                        {[...Array(testimonial.rating)].map((_, i) => (
                                            <Star key={i} size={20} fill="currentColor" />
                                        ))}
                                    </div>

                                    <p className="text-base sm:text-lg text-slate-600 leading-relaxed relative z-10">
                                        "{testimonial.content}"
                                    </p>

                                    <div className="mt-4 flex items-center justify-between">
                                        <div>
                                            <p className="font-bold text-[var(--color-dark)]">
                                                {testimonial.name}
                                            </p>

                                            <p className="text-sm text-slate-500">
                                                {testimonial.company}
                                            </p>
                                        </div>

                                        <div className="w-12 h-1 bg-gradient-to-r from-[var(--color-accent)] to-[var(--color-secondary)] rounded-full group-hover:w-20 transition-all duration-500" />
                                    </div>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </section>

            <div ref={leadTriggerRef} className="h-px" />

            <CTA />

            {/* Lead Modal */}
            <AnimatePresence>
                {showLeadModal && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 px-3 py-3 sm:py-4 backdrop-blur-md"
                        onClick={() => {
                            modalAllowedRef.current = false;
                            setShowLeadModal(false);
                        }}
                    >
                        <motion.div
                            initial={{ opacity: 0, y: 30, scale: 0.95 }}
                            animate={{ opacity: 1, y: 0, scale: 1 }}
                            exit={{ opacity: 0, y: 20, scale: 0.95 }}
                            transition={{ duration: 0.3, ease: 'easeOut' }}
                            className="relative w-full max-w-xl max-h-[92vh] overflow-y-auto overflow-x-hidden rounded-3xl bg-white shadow-[0_50px_150px_rgba(0,0,0,0.3)]"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                type="button"
                                onClick={() => {
                                    modalAllowedRef.current = false;
                                    setShowLeadModal(false);
                                }}
                                className="absolute right-3 top-3 sm:right-4 sm:top-4 z-10 rounded-full bg-white/80 backdrop-blur-sm p-1.5 sm:p-2 text-slate-600 hover:bg-white hover:shadow-lg transition-all"
                                aria-label="Close popup"
                            >
                                <X size={18} className="sm:w-5 sm:h-5" />
                            </button>

                            <div className="relative bg-gradient-to-br from-[var(--color-accent)] via-[var(--color-accent)] to-[var(--color-secondary)] px-5 py-5 sm:px-8 sm:py-10">
                                <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />

                                <div className="relative">
                                    <p className="text-xs sm:text-sm font-bold uppercase tracking-[0.2em] text-white/80">
                                        Get in Touch
                                    </p>

                                    <h3 className="mt-1 sm:mt-2 text-xl sm:text-3xl font-bold text-white">
                                        Request a Quote
                                    </h3>

                                    <p className="mt-1 sm:mt-2 text-sm sm:text-base text-white/70 leading-relaxed">
                                        Fill in the details and our experts will reach out within 24 hours.
                                    </p>
                                </div>
                            </div>

                            <div className="px-5 py-4 sm:px-8 sm:py-8">
                                {leadSubmitted ? (
                                    <div className="flex flex-col items-center justify-center py-6 sm:py-8 text-center">
                                        <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-3 sm:mb-4 animate-bounce shadow-lg shadow-emerald-200">
                                            <CheckCircle2
                                                size={28}
                                                className="sm:w-8 sm:h-8 text-emerald-500"
                                            />
                                        </div>

                                        <h4 className="text-xl sm:text-2xl font-bold text-[var(--color-dark)]">
                                            Thank You!
                                        </h4>

                                        <p className="text-sm sm:text-base text-slate-500 mt-2">
                                            We've received your request and will contact you soon.
                                        </p>
                                    </div>
                                ) : (
                                    <form
                                        onSubmit={handleLeadSubmit}
                                        className="space-y-3 sm:space-y-4"
                                    >
                                        <div className="grid gap-3 sm:gap-4 sm:grid-cols-2">
                                            <div>
                                                <label className="block text-xs sm:text-sm font-semibold text-[var(--color-dark)] mb-1 sm:mb-1.5">
                                                    Full Name
                                                </label>

                                                <input
                                                    type="text"
                                                    name="name"
                                                    value={leadFormData.name}
                                                    onChange={handleLeadChange}
                                                    required
                                                    className="w-full rounded-xl border border-[var(--color-border)] bg-slate-50 px-3 py-2.5 sm:px-4 sm:py-3 text-sm sm:text-base text-[var(--color-dark)] outline-none transition focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/20 focus:bg-white focus:shadow-lg focus:shadow-[var(--color-accent)]/10"
                                                    placeholder="Your name"
                                                />
                                            </div>

                                            <div>
                                                <label className="block text-xs sm:text-sm font-semibold text-[var(--color-dark)] mb-1 sm:mb-1.5">
                                                    Phone Number
                                                </label>

                                                <input
                                                    type="tel"
                                                    name="phone"
                                                    value={leadFormData.phone}
                                                    onChange={handleLeadChange}
                                                    required
                                                    className="w-full rounded-xl border border-[var(--color-border)] bg-slate-50 px-3 py-2.5 sm:px-4 sm:py-3 text-sm sm:text-base text-[var(--color-dark)] outline-none transition focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/20 focus:bg-white focus:shadow-lg focus:shadow-[var(--color-accent)]/10"
                                                    placeholder="+91 96508 46272"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label className="block text-xs sm:text-sm font-semibold text-[var(--color-dark)] mb-1 sm:mb-1.5">
                                                Email Address
                                            </label>

                                            <input
                                                type="email"
                                                name="email"
                                                value={leadFormData.email}
                                                onChange={handleLeadChange}
                                                required
                                                className="w-full rounded-xl border border-[var(--color-border)] bg-slate-50 px-3 py-2.5 sm:px-4 sm:py-3 text-sm sm:text-base text-[var(--color-dark)] outline-none transition focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/20 focus:bg-white focus:shadow-lg focus:shadow-[var(--color-accent)]/10"
                                                placeholder="you@example.com"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-xs sm:text-sm font-semibold text-[var(--color-dark)] mb-1 sm:mb-1.5">
                                                Project Details
                                            </label>

                                            <textarea
                                                name="message"
                                                value={leadFormData.message}
                                                onChange={handleLeadChange}
                                                rows={2}
                                                required
                                                className="w-full rounded-xl border border-[var(--color-border)] bg-slate-50 px-3 py-2.5 sm:px-4 sm:py-3 text-sm sm:text-base text-[var(--color-dark)] outline-none transition focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/20 focus:bg-white focus:shadow-lg focus:shadow-[var(--color-accent)]/10 resize-none"
                                                placeholder="Tell us about your project requirements..."
                                            />
                                        </div>

                                        <button
                                            type="submit"
                                            disabled={leadSubmitting}
                                            className="w-full bg-gradient-to-r from-[var(--color-accent)] to-[var(--color-secondary)] text-white font-bold py-3 rounded-xl hover:shadow-2xl hover:shadow-[var(--color-accent)]/30 transition-all duration-300 hover:scale-[1.02] disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:scale-100"
                                        >
                                            {leadSubmitting ? (
                                                <span className="flex items-center justify-center gap-2">
                                                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                                    Sending...
                                                </span>
                                            ) : (
                                                'Send Request'
                                            )}
                                        </button>
                                    </form>
                                )}
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default Home;